import { numberParam } from '../types/checks.js';
import type { CheckResult, ValidationCheck } from '../types/checks.js';
import { clamp, roundTo } from '../types/geometry.js';
import { analyzeFaceShadow } from '../quality/lightingBalance.js';
import { missingRequirementReason, notChecked, plainResult } from './support.js';

const MIN_FACE_PIXEL_WIDTH = 24;
const MIN_FACE_CELLS = 12;

/**
 * «Тень на лице».
 *
 * Detects *local* dark regions relative to the face median — a shadow under the
 * nose, a shadow from the hair, a hard line across the cheek. This is separate
 * from `lighting_balance`, which only compares halves: a face can be perfectly
 * balanced left/right and still carry a local shadow.
 *
 * Never a hard failure; a shadow is a reason to re-shoot.
 */
export const shadowFaceCheck: ValidationCheck = {
  id: 'shadow_face',
  title: 'Тень на лице',
  description:
    'Ищет локальные тёмные участки на коже относительно медианной яркости лица и резкие границы яркости.',
  kind: 'quality',
  requires: ['pixels', 'geometry'],

  run: (ctx): CheckResult => {
    const missing = missingRequirementReason(ctx, ['pixels', 'geometry']);
    if (missing) return notChecked(ctx, shadowFaceCheck, missing, ctx.rule.severity);
    const pixels = ctx.pixels;
    const geometry = ctx.geometry;
    if (!pixels || !geometry) {
      return notChecked(
        ctx,
        shadowFaceCheck,
        'Недостаточно данных для поиска тени на лице.',
        ctx.rule.severity,
      );
    }

    const shadowDelta = numberParam(ctx.rule.params, 'shadowDelta', 22);
    /**
     * Two spellings of the same bounds are accepted: the descriptive
     * `maxShadowArea`/`minSideDifference` used by the document profiles and the
     * internal `minShadowAreaRatio`/`maxSideDifference`.
     */
    const minShadowAreaRatio = numberParam(
      ctx.rule.params,
      'maxShadowArea',
      numberParam(ctx.rule.params, 'minShadowAreaRatio', 0.06),
    );
    const maxSideDifference = numberParam(
      ctx.rule.params,
      'minSideDifference',
      numberParam(ctx.rule.params, 'maxSideDifference', 28),
    );
    const edgeThreshold = numberParam(ctx.rule.params, 'edgeThreshold', 30);
    const uncertainBelowConfidence = numberParam(ctx.rule.params, 'uncertainBelowConfidence', 0.35);

    const analysis = analyzeFaceShadow({
      buffer: pixels,
      geometry,
      shadowDelta,
    });
    const facePixelWidth = geometry.faceBox.width * pixels.width;
    const sizeScore = clamp(analysis.sampleCells / 24, 0, 1);
    const confidence = clamp(
      0.3 + geometry.faceBoxConfidence.value * 0.5 + sizeScore * 0.2,
      0,
      1,
    );
    const tooSmall =
      facePixelWidth < MIN_FACE_PIXEL_WIDTH ||
      analysis.sampleCells < MIN_FACE_CELLS ||
      confidence < uncertainBelowConfidence;

    const areaShadowed = analysis.shadowAreaRatio > minShadowAreaRatio;
    const hardSideShadow =
      analysis.sideDifference > maxSideDifference && analysis.edgeStrength > edgeThreshold;

    const status: CheckResult['status'] = tooSmall
      ? 'uncertain'
      : areaShadowed || hardSideShadow
        ? 'warning'
        : 'pass';

    return plainResult({
      id: shadowFaceCheck.id,
      title: shadowFaceCheck.title,
      status,
      severity: ctx.rule.severity,
      value: roundTo(analysis.shadowAreaRatio, 4),
      unit: 'ratio',
      message:
        status === 'pass'
          ? '✅ Тени на лице не обнаружены'
          : status === 'warning'
            ? '⚠ На лице есть тень'
            : '⚠ Лицо на снимке слишком мелкое, чтобы надёжно найти тень.',
      requirement:
        'Лицо должно быть освещено ровно, без локальных теней и резких перепадов яркости на коже.',
      fixability: 'retake',
      geometry: analysis.regions.map((region) => ({
        type: 'rect' as const,
        coordinates: [
          roundTo(region.rect.x, 4),
          roundTo(region.rect.y, 4),
          roundTo(region.rect.width, 4),
          roundTo(region.rect.height, 4),
        ] as [number, number, number, number],
      })),
      problemRegions:
        status === 'warning'
          ? analysis.regions.map((region) => ({
              id: region.id,
              rect: {
                x: roundTo(region.rect.x, 4),
                y: roundTo(region.rect.y, 4),
                width: roundTo(region.rect.width, 4),
                height: roundTo(region.rect.height, 4),
              },
              message: 'На этом участке лица тень.',
              severity: 'warning' as const,
            }))
          : [],
      confidence: roundTo(confidence, 2),
      details: {
        shadowAreaRatio: roundTo(analysis.shadowAreaRatio, 4),
        sideDifference: roundTo(analysis.sideDifference, 1),
        edgeStrength: roundTo(analysis.edgeStrength, 1),
        faceMedianLuma: roundTo(analysis.faceMedianLuma, 1),
        leftMean: roundTo(analysis.leftMean, 1),
        rightMean: roundTo(analysis.rightMean, 1),
        sampleCells: analysis.sampleCells,
        regions: analysis.regions.map((region) => ({
          id: region.id,
          meanLuma: region.meanLuma,
          delta: region.delta,
          rect: region.rect,
        })),
        limits: { shadowDelta, minShadowAreaRatio, maxSideDifference, edgeThreshold },
        areaShadowed,
        hardSideShadow,
        displayValue: `${roundTo(analysis.shadowAreaRatio * 100, 0).toFixed(0)} %`,
      },
    });
  },
};
